import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import localStorageService from "utils/localStorageService";
import { ONBOARDING_STEP_KEY } from "@shared/features/onboarding/constants";
import { ONBOARDING_DETAILED_STEP_TO_PATH } from "features/onboarding/common/constants";
import { GetUpdatedPathType } from "types";

const resumeKey = (userId: number) => `onboarding-resume:${userId}`;
const resumeParameters = [
  "method",
  "groupID",
  "botUUID",
  "guided",
  "setup",
  "exchange",
  "account-id",
  "startMode",
  "connectLive",
  "preset",
  "customDemo",
];

export const getOnboardingResumePath = (userId: number): string | undefined => {
  try {
    const path = localStorageService.getValue(resumeKey(userId));
    if (!path) return;
    const [pathname] = path.split("?");
    if (Object.values(ONBOARDING_DETAILED_STEP_TO_PATH).includes(pathname))
      return path;
  } catch {}
};

export const saveOnboardingResumePath = (
  userId: number,
  path: string
): void => {
  const [pathname, search] = path.split("?");
  const query = new URLSearchParams(search);
  const savedQuery = new URLSearchParams();
  resumeParameters.forEach((name) => {
    if (query.has(name)) savedQuery.set(name, query.get(name)!);
  });
  try {
    localStorageService.setValue(
      resumeKey(userId),
      `${pathname}?${savedQuery}`
    );
  } catch {}
};

export const clearOnboardingResumePath = (userId: number): void => {
  localStorageService.removeValue(resumeKey(userId));
};

export const getOnboardingLocalStorageValue = () => {
  const onboardingLocalStorageValue: string | null =
    localStorageService.getValue(ONBOARDING_STEP_KEY);
  if (onboardingLocalStorageValue)
    return JSON.parse(onboardingLocalStorageValue);
};

export const updateOnboardingLocalStorageKey = (
  userId: number,
  step: OnboardingDetailedStep
) => {
  let userToStepMapping = getOnboardingLocalStorageValue() || {};
  userToStepMapping = { ...userToStepMapping, [userId]: step };

  localStorageService.setValue(
    ONBOARDING_STEP_KEY,
    JSON.stringify(userToStepMapping)
  );
};

export const changeOnboardingStep = (
  userId: number | undefined,
  step: OnboardingDetailedStep,
  historyPush: (path: string) => void,
  getUpdatedPath?: GetUpdatedPathType,
  queryToAppend?: Record<string, string>,
  saveResume = false
) => {
  if (userId) {
    updateOnboardingLocalStorageKey(userId, step);
  }
  let path = ONBOARDING_DETAILED_STEP_TO_PATH[step];
  if (getUpdatedPath && queryToAppend) {
    path = getUpdatedPath(queryToAppend, undefined, path);
  }
  if (userId && saveResume) saveOnboardingResumePath(userId, path);
  historyPush(path);
};

// Per-tab proof that `connectLive=1` came from the in-app live choice, not a
// link: it survives a refresh but not a link opened in a new tab.
const LIVE_CHOICE_KEY = "onboarding-live-choice";

export const markOnboardingLiveChoice = (): void => {
  try {
    sessionStorage.setItem(LIVE_CHOICE_KEY, "1");
  } catch {
    // Storage unavailable: a refresh then keeps the current workspace.
  }
};

export const hasOnboardingLiveChoice = (): boolean => {
  try {
    return sessionStorage.getItem(LIVE_CHOICE_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearOnboardingLiveChoice = (): void => {
  try {
    sessionStorage.removeItem(LIVE_CHOICE_KEY);
  } catch {
    // Storage unavailable: nothing was stored.
  }
};

export const didUserSkipOnboarding = (userId: number) => {
  return (
    getOnboardingLocalStorageValue()?.[userId] === OnboardingDetailedStep.SKIP
  );
};
